import React, { useEffect } from 'react';
import { Modal, ModalMedia, ModalFooter } from '@lux/modal';
import { Celebration, GraphicSize } from '@lux/graphics';
import { PlusTierPill, Size } from '@lux/plus-badge';
import { Translate, useFormatters } from '@pegasus/translation';
import { ComplementaryColor, screenSizes } from '@lux/core';
import { useSelector } from 'react-redux';
import { getClientDeviceWidth } from '@client/redux/modules/clientDevice/selectors';
import { trackSelectContent, trackViewContent } from '@browsing/tracking';
import { TitlePreset } from '@lux/title';
import { useLoyalty } from '@client/components/mylounge/hooks/use-loyalty';
import { useConfig } from '@browsing/config';
import {
    ModalDescription,
    ModalTitle,
    ModalOverlay,
    ModalWrapper,
    PlusBadgeWrapper,
} from './styles';

interface MemberFreeDeliveryDialogProps {
    open: boolean;
    onClose: () => void;
    minimumOrderValue: number;
}

export const MemberFreeDeliveryDialog: React.FC<MemberFreeDeliveryDialogProps> = ({
    open,
    onClose,
    minimumOrderValue = 79,
}) => {
    const deviceWidth = useSelector(getClientDeviceWidth);
    const isMobileView = deviceWidth < screenSizes.sm;
    const loyalty = useLoyalty();
    const config = useConfig();
    const tier = loyalty.summary.tier;
    const { formatPrice } = useFormatters();

    useEffect(() => {
        if (open) {
            trackViewContent('loyalty_free_delivery');
        }
    }, [open]);

    return (
        <Modal
            dismissable
            open={open}
            onOpenChange={onClose}
            purpose="NON_COMMERCIAL"
            role="dialog"
            size="S"
            sizeSm="S"
            mobileFullScreen={isMobileView}
        >
            <ModalOverlay
                dismissLabel="Close dialog"
                withoutCloseIcon
                onClick={() => {
                    trackSelectContent('loyalty_free_delivery', 'click', {
                        info: 'click',
                        contentId: 'close',
                    });
                    onClose();
                }}
            >
                <ModalWrapper onClick={e => e.stopPropagation()} isMember={true}>
                    <PlusBadgeWrapper>
                        <PlusTierPill tier={tier} size={Size.M} dynamicFontSize>
                            <Translate message={`plus.loyalty.tier.${tier}`} />
                        </PlusTierPill>
                    </PlusBadgeWrapper>

                    <ModalMedia>
                        <Celebration size={GraphicSize.L} bgColor={ComplementaryColor.brand30} />
                    </ModalMedia>

                    <ModalTitle preset={TitlePreset.S} presetSm={TitlePreset.XS}>
                        <Translate
                            message="free_delivery.member.popup.title"
                            params={{
                                minimumOrderValue: formatPrice({
                                    cents: minimumOrderValue * 100,
                                    maximumFractionDigits: 0,
                                }),
                            }}
                        />
                    </ModalTitle>

                    <ModalDescription>
                        <Translate message="free_delivery.member.popup.description" />
                    </ModalDescription>

                    <ModalFooter
                        primaryAction={{
                            children: <Translate message="free_delivery.member.popup.shop" />,
                            href: '#',
                            link: true,
                            onClick: () => {
                                trackSelectContent('loyalty_free_delivery', 'click', {
                                    info: 'click',
                                    contentId: 'shop',
                                });
                                onClose();
                            },
                        }}
                    />
                </ModalWrapper>
            </ModalOverlay>
        </Modal>
    );
};
